/**
 * IMBEE-9563 — when the channel's configured greeting should be shown.
 *
 * Extracted from the stomp connectCallback so the decision can be tested. It lived inline,
 * which meant the only way to exercise it was a live broker; this repo's Test job is blocking,
 * so the four cases below now gate permanently.
 *
 * The product answer this encodes (IMBEE-9563 AC-1): show the greeting whenever a conversation
 * opens with NO messages — not once per visitor. That covers a new visitor and a returning one
 * whose history is empty. The consequence worth knowing is that a returning visitor whose
 * history was cleared is greeted again.
 */
export type WelcomeMessageDecision = {
    /** Messages just loaded from the server. 0 = empty conversation, undefined = fetch failed. */
    loadedCount: number | undefined;
    /** What the message list holds right now, read at the moment of writing. */
    existingMessageCount: number;
    /** The channel's configured greeting; empty when the channel has none. */
    welcomeMessage: string;
};

export const shouldShowWelcomeMessage = ({
    loadedCount,
    existingMessageCount,
    welcomeMessage,
}: WelcomeMessageDecision): boolean => {
    // undefined means the fetch FAILED, which is not an empty conversation. Painting a greeting
    // over history we merely could not load would misrepresent the conversation to the visitor.
    if (loadedCount !== 0) return false;

    // A channel with no greeting configured shows nothing rather than an empty bubble.
    if (!welcomeMessage) return false;

    // The live list, not just the fetch result: the append happens after an await, so two
    // overlapping connects could both see loadedCount === 0. Also covers a message arriving in
    // that gap. Defensive — the interleaving is reachable by inspection, not observed.
    if (existingMessageCount > 0) return false;

    return true;
};
